import { RECT_WIPE_CLOSED, RECT_WIPE_OPEN, OVERLAY_WIPE_REDUCED_SECONDS, MENU_WIPE_OPEN_SECONDS, MENU_WIPE_CLOSE_RATE } from './motion-tokens.js';

export function createDirectionalScrollIntent({
    topThreshold = 24,
    hideThreshold = 140,
    showDistance = 36,
    hideDistance = 24,
    minDelta = 1,
} = {}) {
    let last = 0;
    let direction = 0;
    let travel = 0;

    return {
        reset(scrollY = 0) {
            last = Math.max(0, scrollY || 0);
            direction = 0;
            travel = 0;
        },
        update(scrollY = 0) {
            const current = Math.max(0, scrollY || 0);
            const delta = current - last;
            last = current;

            if (current <= topThreshold) {
                direction = 0;
                travel = 0;
                return { action: 'top', current };
            }
            if (Math.abs(delta) < minDelta) return { action: 'none', current };

            const nextDirection = delta > 0 ? 1 : -1;
            if (nextDirection !== direction) {
                direction = nextDirection;
                travel = 0;
            }
            travel += Math.abs(delta);

            if (direction > 0 && current > hideThreshold && travel >= hideDistance) {
                travel = 0;
                return { action: 'hide', current };
            }
            if (direction < 0 && travel >= showDistance) {
                travel = 0;
                return { action: 'show', current };
            }
            return { action: 'none', current };
        },
    };
}

export function initNavigation({ lenis = null, gsap = null, reducedMotion = false } = {}) {
    const root = document.documentElement;
    const body = document.body;
    const header = document.querySelector('[data-site-header]');
    const headerBar = header?.querySelector('[data-header-bar]');
    const brandShell = document.querySelector('[data-site-brand-shell]');
    const headerMotionTargets = [header, brandShell].filter(Boolean);
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const menuLabel = document.querySelector('[data-menu-label]');
    const navigation = document.querySelector('[data-primary-navigation]');
    const menuSurface = navigation?.querySelector('[data-menu-surface]');
    const menuScroll = navigation?.querySelector('[data-menu-scroll]');
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchPanel = document.querySelector('[data-search-panel]');
    const searchInput = searchPanel?.querySelector('input[type="search"]');
    const searchClose = searchPanel?.querySelector('[data-search-close]');
    const openLabel = menuToggle?.dataset.labelOpen || 'Menu';
    const closeLabel = menuToggle?.dataset.labelClose || 'Close';
    const intent = createDirectionalScrollIntent();

    let headerVisible = true;
    let menuTimeline = null;
    let scrollLocked = false;
    let raf = 0;

    navigation?.setAttribute('data-lenis-prevent', '');
    searchPanel?.setAttribute('data-lenis-prevent', '');

    const menuGroups = () => ({
        eyebrows: navigation ? [...navigation.querySelectorAll('.menu-overlay__eyebrow')] : [],
        primary: navigation ? [...navigation.querySelectorAll('.primary-menu > li > a')] : [],
        external: navigation ? [...navigation.querySelectorAll('.external-menu > li > a')] : [],
        contact: navigation ? [...navigation.querySelectorAll('.menu-overlay__contact-grid > *')] : [],
        social: navigation ? [...navigation.querySelectorAll('.social-menu--overlay > li > a')] : [],
    });

    const getHeaderBaseTop = () => Math.ceil(document.getElementById('wpadminbar')?.getBoundingClientRect().height || 0);
    const getHeaderHiddenTop = () => {
        const height = Math.ceil(Math.max(header?.getBoundingClientRect().height || 0, brandShell?.getBoundingClientRect().height || 0));
        const safety = window.matchMedia('(max-width: 767px)').matches ? 40 : 28;
        return getHeaderBaseTop() - height - safety;
    };

    const measureHeader = () => {
        const height = headerVisible ? Math.ceil(headerBar?.getBoundingClientRect().height || 0) : 0;
        root.style.setProperty('--led-header-offset', `${height}px`);
    };

    const setHeaderMode = (mode) => {
        if (!header) return;
        const next = mode === 'compact' ? 'compact' : 'hero';
        if (header.dataset.headerMode === next) return;
        header.dataset.headerMode = next;
        if (brandShell) brandShell.dataset.headerMode = next;
        window.requestAnimationFrame(() => window.requestAnimationFrame(measureHeader));
    };

    const showHeader = ({ mode = 'compact', force = false } = {}) => {
        if (!header || body.classList.contains('city-explorer-is-active')) return;
        setHeaderMode(mode);
        if (headerVisible && !force) return;
        headerVisible = true;
        const shownTop = getHeaderBaseTop();

        if (gsap && !reducedMotion) {
            gsap.killTweensOf(headerMotionTargets);
            gsap.to(headerMotionTargets, {
                top: shownTop,
                duration: 0.82,
                ease: 'power4.out',
                overwrite: true,
                onUpdate: measureHeader,
            });
        } else {
            headerMotionTargets.forEach((target) => { target.style.top = `${shownTop}px`; });
        }
        measureHeader();
    };

    const hideHeader = ({ force = false } = {}) => {
        if (!header || (!headerVisible && !force)) return;
        if (body.classList.contains('menu-is-open') || body.classList.contains('search-is-open')) return;
        headerVisible = false;
        root.style.setProperty('--led-header-offset', '0px');
        const hiddenTop = getHeaderHiddenTop();

        if (gsap && !reducedMotion) {
            gsap.killTweensOf(headerMotionTargets);
            gsap.to(headerMotionTargets, {
                top: hiddenTop,
                duration: 0.68,
                ease: 'power3.inOut',
                overwrite: true,
            });
        } else {
            headerMotionTargets.forEach((target) => { target.style.top = `${hiddenTop}px`; });
        }
    };

    const updateHeader = () => {
        raf = 0;
        if (!header || body.classList.contains('city-explorer-is-active') || body.classList.contains('menu-is-open')) return;
        const scrollY = Math.max(0, window.scrollY || 0);
        const result = intent.update(scrollY);

        if (result.action === 'top') {
            showHeader({ mode: 'hero', force: true });
            return;
        }
        if (result.action === 'show') {
            showHeader({ mode: 'compact' });
            return;
        }
        if (result.action === 'hide') hideHeader();
    };

    const requestHeaderUpdate = () => {
        if (!raf) raf = window.requestAnimationFrame(updateHeader);
    };

    const setOverlayScrollLock = (locked) => {
        const next = Boolean(locked);
        if (next === scrollLocked) return;
        scrollLocked = next;
        if (next) lenis?.stop?.();
        else lenis?.start?.();
    };

    const syncOverlayLock = () => setOverlayScrollLock(
        body.classList.contains('menu-is-open') || body.classList.contains('search-is-open')
    );

    const preventBackgroundScroll = (event) => {
        if (!scrollLocked) return;
        if (navigation?.contains(event.target) || searchPanel?.contains(event.target)) return;
        event.preventDefault();
    };
    window.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    window.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

    const finishMenuClosed = ({ restoreFocus = false } = {}) => {
        if (!navigation || !menuToggle) return;
        body.classList.remove('menu-is-open');
        navigation.setAttribute('aria-hidden', 'true');
        menuToggle.setAttribute('aria-expanded', 'false');
        if (menuLabel) menuLabel.textContent = openLabel;
        navigation.setAttribute('inert', '');
        if (gsap) {
            gsap.set(navigation, { pointerEvents: 'none' });
        } else {
            navigation.style.pointerEvents = 'none';
            navigation.style.clipPath = 'polygon(0 0,100% 0,100% 0,0 0)';
        }
        syncOverlayLock();
        if (restoreFocus) menuToggle.focus({ preventScroll: true });
    };

    const buildMenuTimeline = () => {
        if (!navigation || !menuSurface || !menuScroll || !gsap) return null;

        const groups = menuGroups();
        const divider = navigation.querySelector('.menu-overlay__divider');
        const wipeDuration = reducedMotion ? OVERLAY_WIPE_REDUCED_SECONDS : MENU_WIPE_OPEN_SECONDS;
        const motionScale = reducedMotion ? 1 : wipeDuration / 0.86;
        const duration = (value) => value * motionScale;
        const fitStagger = (items, start, itemDuration, preferred, end) => {
            if (reducedMotion || items.length <= 1) return 0;
            const available = Math.max(0, duration(end - start - itemDuration));
            return Math.min(duration(preferred), available / (items.length - 1));
        };

        // Keep the same rectangular wipe language as Gallery / 3D, but allow the
        // navigation a slightly longer breath so the stagger remains cinematic.
        gsap.set(navigation, {
            autoAlpha: 1,
            pointerEvents: 'none',
            clipPath: RECT_WIPE_CLOSED,
            force3D: true,
        });
        navigation.setAttribute('inert', '');
        gsap.set([menuSurface, menuScroll], { clearProps: 'clipPath' });
        gsap.set(menuSurface, { autoAlpha: 1 });
        gsap.set(groups.eyebrows, { y: 10, autoAlpha: 0 });
        gsap.set(groups.primary, { yPercent: 88, autoAlpha: 0, force3D: true });
        gsap.set(groups.external, { yPercent: 72, autoAlpha: 0, force3D: true });
        gsap.set(groups.contact, { y: 16, autoAlpha: 0, force3D: true });
        gsap.set(groups.social, { y: 10, autoAlpha: 0, force3D: true });
        if (divider) gsap.set(divider, { scaleX: 0, transformOrigin: '0% 50%' });

        return gsap.timeline({
            paused: true,
            defaults: { overwrite: 'auto' },
            onReverseComplete: () => finishMenuClosed(),
        })
            .set(navigation, { pointerEvents: 'auto' }, 0)
            .to(navigation, {
                clipPath: RECT_WIPE_OPEN,
                duration: wipeDuration,
                ease: reducedMotion ? 'none' : 'power4.inOut',
                force3D: true,
            }, 0)
            .to(groups.eyebrows, {
                y: 0,
                autoAlpha: 1,
                duration: duration(0.28),
                stagger: fitStagger(groups.eyebrows, 0.10, 0.28, 0.045, 0.46),
                ease: 'power3.out',
            }, duration(0.10))
            .to(groups.primary, {
                yPercent: 0,
                autoAlpha: 1,
                duration: duration(0.34),
                stagger: fitStagger(groups.primary, 0.15, 0.34, 0.05, 0.80),
                ease: 'power4.out',
            }, duration(0.15))
            .to(groups.external, {
                yPercent: 0,
                autoAlpha: 1,
                duration: duration(0.30),
                stagger: fitStagger(groups.external, 0.24, 0.30, 0.05, 0.62),
                ease: 'power4.out',
            }, duration(0.24))
            .to(divider, {
                scaleX: 1,
                duration: duration(0.42),
                ease: 'power3.inOut',
            }, duration(0.28))
            .to(groups.contact, {
                y: 0,
                autoAlpha: 1,
                duration: duration(0.30),
                stagger: fitStagger(groups.contact, 0.36, 0.30, 0.04, 0.76),
                ease: 'power3.out',
            }, duration(0.36))
            .to(groups.social, {
                y: 0,
                autoAlpha: 1,
                duration: duration(0.24),
                stagger: fitStagger(groups.social, 0.49, 0.24, 0.035, 0.84),
                ease: 'power3.out',
            }, duration(0.49));
    };

    const setSearchOpen = (open, restoreFocus = false) => {
        if (!searchToggle || !searchPanel || (open && body.classList.contains('city-explorer-is-active'))) return;
        if (open && body.classList.contains('menu-is-open')) setMenuOpen(false);

        searchToggle.setAttribute('aria-expanded', String(open));
        searchPanel.setAttribute('aria-hidden', String(!open));
        searchPanel.dataset.open = String(open);
        body.classList.toggle('search-is-open', open);

        if (gsap && !reducedMotion) {
            gsap.killTweensOf(searchPanel);
            if (open) {
                gsap.set(searchPanel, { autoAlpha: 1, pointerEvents: 'auto', clipPath: RECT_WIPE_CLOSED, force3D: true });
                gsap.to(searchPanel, { clipPath: RECT_WIPE_OPEN, duration: 0.78, ease: 'power4.inOut', force3D: true });
            } else {
                gsap.to(searchPanel, {
                    clipPath: RECT_WIPE_CLOSED,
                    duration: 0.62,
                    ease: 'power4.inOut',
                    force3D: true,
                    onComplete: () => gsap.set(searchPanel, { autoAlpha: 0, pointerEvents: 'none' }),
                });
            }
        } else {
            searchPanel.style.visibility = open ? 'visible' : 'hidden';
            searchPanel.style.opacity = open ? '1' : '0';
            searchPanel.style.pointerEvents = open ? 'auto' : 'none';
            searchPanel.style.clipPath = open ? RECT_WIPE_OPEN : RECT_WIPE_CLOSED;
        }

        if (open) {
            window.setTimeout(() => searchInput?.focus({ preventScroll: true }), reducedMotion ? 0 : 430);
        } else if (restoreFocus) {
            searchToggle.focus({ preventScroll: true });
        }
        syncOverlayLock();
    };

    const setMenuOpen = (open, restoreFocus = false) => {
        if (!navigation || !menuToggle || (open && body.classList.contains('city-explorer-is-active'))) return;

        if (open) {
            setSearchOpen(false);
            body.classList.add('menu-is-open');
            navigation.setAttribute('aria-hidden', 'false');
            navigation.removeAttribute('inert');
            menuToggle.setAttribute('aria-expanded', 'true');
            if (menuLabel) menuLabel.textContent = closeLabel;
            showHeader({ mode: window.scrollY > 24 ? 'compact' : 'hero', force: true });
            syncOverlayLock();

            if (!gsap || !menuSurface || !menuScroll) {
                navigation.style.opacity = '1';
                navigation.style.visibility = 'visible';
                navigation.style.pointerEvents = 'auto';
                navigation.style.clipPath = 'polygon(0 0,100% 0,100% 100%,0 100%)';
                return;
            }

            if (!menuTimeline) menuTimeline = buildMenuTimeline();
            menuTimeline?.timeScale(1).play();
            return;
        }

        if (!body.classList.contains('menu-is-open')) return;
        menuToggle.setAttribute('aria-expanded', 'false');
        if (menuLabel) menuLabel.textContent = openLabel;

        if (!gsap || !menuTimeline) {
            finishMenuClosed({ restoreFocus });
            return;
        }

        if (restoreFocus) {
            const previous = menuTimeline.eventCallback('onReverseComplete');
            menuTimeline.eventCallback('onReverseComplete', () => {
                finishMenuClosed({ restoreFocus: true });
                menuTimeline.eventCallback('onReverseComplete', previous);
            });
        }
        menuTimeline.timeScale(MENU_WIPE_CLOSE_RATE).reverse();
    };

    menuToggle?.addEventListener('click', () => setMenuOpen(menuToggle.getAttribute('aria-expanded') !== 'true'));
    navigation?.addEventListener('click', (event) => {
        if (event.target.closest('a')) setMenuOpen(false);
    });
    searchToggle?.addEventListener('click', () => setSearchOpen(searchToggle.getAttribute('aria-expanded') !== 'true'));
    searchClose?.addEventListener('click', () => setSearchOpen(false, true));

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (body.classList.contains('menu-is-open')) setMenuOpen(false, true);
        else if (body.classList.contains('search-is-open')) setSearchOpen(false, true);
    });

    document.addEventListener('led:citystate', (event) => {
        const active = Boolean(event.detail?.active);
        if (active) {
            if (body.classList.contains('menu-is-open')) {
                menuTimeline?.pause(0);
                finishMenuClosed();
            }
            if (body.classList.contains('search-is-open')) setSearchOpen(false);
            headerVisible = false;
            root.style.setProperty('--led-header-offset', '0px');
            if (gsap) gsap.set(header, { top: getHeaderHiddenTop(), clearProps: 'transform' });
            else if (header) header.style.top = `${getHeaderHiddenTop()}px`;
            return;
        }

        const scrollY = Math.max(0, window.scrollY || 0);
        intent.reset(scrollY);
        headerVisible = false;
        showHeader({ mode: scrollY <= 24 ? 'hero' : 'compact', force: true });
    });

    headerBar?.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'min-height' || event.propertyName === 'padding-top' || event.propertyName === 'padding-bottom') measureHeader();
    });

    window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
    window.addEventListener('resize', measureHeader, { passive: true });

    const initialScroll = Math.max(0, window.scrollY || 0);
    intent.reset(initialScroll);
    setHeaderMode(initialScroll <= 24 ? 'hero' : 'compact');
    headerVisible = true;

    if (header) {
        header.style.transform = 'none';
        if (gsap) gsap.set(header, { top: getHeaderBaseTop(), clearProps: 'transform' });
        else header.style.top = `${getHeaderBaseTop()}px`;
    }

    menuTimeline = buildMenuTimeline();
    measureHeader();

    return { showHeader, hideHeader, refreshHeaderOffset: measureHeader };
}
